import { useEffect, useState } from "react";
import { Megaphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { del, errorMessage, get, put } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const UPDATED_EVENT = "platform-dashboard-alert-updated";
const ALERT_ROLES = [["manager", "Managers"], ["locksmith", "Locksmiths"], ["agent", "Agents"], ["engineer", "Engineers"], ["dog_handler", "Dog Handlers"]];

export const DashboardAlertAdmin = () => {
  const [message, setMessage] = useState("");
  const [roles, setRoles] = useState(ALERT_ROLES.map(([role]) => role));
  const [activeAlert, setActiveAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    get("/dashboard-alert").then((alert) => {
      if (!active) return;
      setActiveAlert(alert);
      setMessage(alert?.message || "");
      setRoles(alert?.roles || ALERT_ROLES.map(([role]) => role));
    }).catch((e) => {
      if (active) setError(errorMessage(e));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, []);

  const publish = async () => {
    setBusy(true);
    setError("");
    try {
      const saved = await put("/platform/dashboard-alert", { message: message.trim(), roles });
      setActiveAlert(saved);
      setMessage(saved.message);
      setRoles(saved.roles);
      window.dispatchEvent(new Event(UPDATED_EVENT));
      toast.success("Dashboard alert published");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    setError("");
    try {
      await del("/platform/dashboard-alert");
      setActiveAlert(null);
      setMessage("");
      window.dispatchEvent(new Event(UPDATED_EVENT));
      toast.success("Dashboard alert cleared");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-3xl space-y-4" data-testid="dashboard-alert-admin">
      <div>
        <h2 className="font-display text-lg font-bold text-slate-900">Dashboard alert</h2>
        <p className="mt-1 text-sm text-slate-500">Publish a message for users across all companies.</p>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading alert…</p> : <>
        {activeAlert && <p className="text-sm font-medium text-emerald-700" data-testid="dashboard-alert-active-status">An alert is currently published.</p>}
        <div>
          <Label htmlFor="platform-dashboard-alert-message">Alert message</Label>
          <Textarea id="platform-dashboard-alert-message" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} rows={4}
            className="mt-1.5 bg-white" data-testid="dashboard-alert-message-input" />
          <p className="mt-1 text-right text-xs text-slate-500">{message.length}/500</p>
        </div>
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-slate-900">Visible to roles</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {ALERT_ROLES.map(([role, label]) => (
              <label key={role} className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 p-3 text-sm font-medium text-slate-800">
                <Checkbox checked={roles.includes(role)} onCheckedChange={(checked) => setRoles((current) => checked
                  ? [...current, role]
                  : current.filter((value) => value !== role))} data-testid={`dashboard-alert-role-${role}`} />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
        {error && <p role="alert" className="text-sm text-rose-700" data-testid="dashboard-alert-admin-error">{error}</p>}
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={publish} disabled={busy || loading || !message.trim() || !roles.length} className="bg-blue-700 hover:bg-blue-800" data-testid="dashboard-alert-publish">
            <Megaphone className="mr-2 h-4 w-4" />Publish alert
          </Button>
          <Button type="button" variant="outline" onClick={clear} disabled={busy || loading || !activeAlert} data-testid="dashboard-alert-clear">
            <Trash2 className="mr-2 h-4 w-4" />Clear alert
          </Button>
        </div>
      </>}
    </section>
  );
};